import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Copy, Plus, Send, Trash2, FileSignature, Edit3, XCircle, FileText, RotateCcw,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { useAuth } from "@/contexts/AuthContext";
import { CustomerAPI, EstimateAPI, JobCardAPI, PartAPI, extractError } from "@/lib/api";
import { TaxBreakdownTable } from "@/components/TaxBreakdownTable";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;
const STATUS_PILL = {
    draft: "border-zinc-300 bg-zinc-50 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    sent_for_approval: "border-brand-red bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/10 dark:text-brand-redSoft",
    partially_approved: "border-brand-red bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/10 dark:text-brand-redSoft",
    approved: "border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
    rejected: "border-rose-300 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
    converted_to_invoice: "border-violet-300 bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300",
    cancelled: "border-zinc-300 bg-zinc-100 text-zinc-600 line-through",
    superseded: "border-zinc-300 bg-zinc-100 text-zinc-600",
};

const useTitle = (label) => {
    useEffect(() => {
        document.title = `AutoBrix · ${label}`;
        return () => { document.title = "AutoBrix"; };
    }, [label]);
};

const GST_SLABS = [0, 3, 5, 12, 18, 28];

// ===== List =====
export default function EstimatesPage() {
    useTitle("Estimates");
    const { branches, permissions } = useAuth();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("");
    const [branchId, setBranchId] = useState("");
    const [loading, setLoading] = useState(true);
    const canCreate = permissions.includes("estimate:create");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await EstimateAPI.list({
                status: status || undefined,
                branch_id: branchId || undefined,
                page_size: 200,
            });
            setItems(d.items || []);
        } finally { setLoading(false); }
    }, [status, branchId]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="estimates-page">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Quotations</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight">Estimates</h1>
                    <p className="mt-1 text-sm text-zinc-500">Send line-by-line approval links to customers, then convert atomically into a GST invoice.</p>
                </div>
                {canCreate && (
                    <Link to="/estimates/new"><Button data-testid="estimate-new-btn" className="bg-brand-red text-white hover:bg-brand-redDeep"><Plus className="mr-1 h-4 w-4" />New estimate</Button></Link>
                )}
            </header>

            <div className="flex flex-wrap items-center gap-2">
                <Select value={status} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[220px]" data-testid="estimates-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All statuses</SelectItem>
                        {Object.keys(STATUS_PILL).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={branchId} onValueChange={(v) => setBranchId(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[220px]" data-testid="estimates-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All branches</SelectItem>
                        {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <span className="font-mono text-xs text-zinc-500" data-testid="estimates-total-count">{loading ? "…" : `${items.length} estimates`}</span>
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="estimates-table">
                    <TableHeader><TableRow>
                        <TableHead>Estimate #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Created</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                        {items.map((e) => (
                            <TableRow key={e.id} data-testid={`estimate-row-${e.id}`} className="cursor-pointer"
                                       onClick={() => (window.location.href = `/estimates/${e.id}`)}>
                                <TableCell className="font-mono">{e.estimate_number}</TableCell>
                                <TableCell>{e.customer_snapshot?.display_name || "—"}</TableCell>
                                <TableCell>
                                    <span data-testid={`estimate-status-pill-${e.status}`}
                                          className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-mono ${STATUS_PILL[e.status] || ""}`}>
                                        {e.status.replace(/_/g, " ")}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right font-mono">{inr(e.total_minor)}</TableCell>
                                <TableCell className="text-xs font-mono text-zinc-500">
                                    {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">No estimates yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ===== New =====
export function EstimateNewPage() {
    useTitle("New estimate");
    const [searchParams] = useSearchParams();
    const seedJobId = searchParams.get("job_card_id") || "";
    const nav = useNavigate();
    const { branches, currentBranchId } = useAuth();
    const [branchId, setBranchId] = useState(currentBranchId || "");
    const [customers, setCustomers] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [jobCardId, setJobCardId] = useState(seedJobId);
    const [lines, setLines] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [prefilled, setPrefilled] = useState(false);

    useEffect(() => {
        CustomerAPI.list({ page_size: 100 }).then((d) => setCustomers(d.items || []));
    }, []);

    // Pre-fill from job_card_id (parts_consumed + time_logs)
    useEffect(() => {
        if (!seedJobId) return;
        (async () => {
            try {
                const job = await JobCardAPI.get(seedJobId);
                if (job?.customer?.id) setCustomerId(job.customer.id);
                if (job?.branch_id) setBranchId(job.branch_id);

                const seeded = [];
                let i = 1;
                // labour from time_logs
                let labourLine = null;
                const tl = await JobCardAPI.timeLogs(seedJobId).catch(() => null);
                if (tl?.items?.length) {
                    const totalSec = (tl.items || []).reduce((s, e) => s + (e.duration_seconds || 0), 0);
                    const hours = Math.max(1, Math.round(totalSec / 3600));
                    const branch = (branches || []).find((b) => b.id === job.branch_id);
                    const rate = Number(branch?.labour_rate_per_hour_minor || 50000); // ₹500 fallback
                    labourLine = {
                        line_no: i++, line_type: "labour", description: "Workshop labour (auto from time-logs)",
                        quantity: hours, unit_price_minor: rate, tax_rate_percent: 18,
                        hsn_sac_code: "9987", unit_of_measure: "hr",
                    };
                    seeded.push(labourLine);
                }
                // parts from parts_consumed
                for (const p of (job?.parts_consumed || [])) {
                    seeded.push({
                        line_no: i++, line_type: "part",
                        part_id: p.part_id, description: p.description || p.part_name || "Part",
                        quantity: Number(p.quantity || 1),
                        unit_price_minor: Number(p.unit_price_minor || p.unit_cost_minor_at_consumption || 0),
                        tax_rate_percent: Number(p.tax_rate_percent || 18),
                        hsn_sac_code: p.hsn_sac_code || "8708",
                        unit_of_measure: p.unit_of_measure || "piece",
                    });
                }
                setLines(seeded.length ? seeded : [{ line_no: 1, line_type: "misc", description: "", quantity: 1, unit_price_minor: 0, tax_rate_percent: 18, hsn_sac_code: "9987" }]);
                setPrefilled(seeded.length > 0);
            } catch (e) {
                toast.error("Could not pre-fill from job: " + extractError(e));
                setLines([{ line_no: 1, line_type: "misc", description: "", quantity: 1, unit_price_minor: 0, tax_rate_percent: 18, hsn_sac_code: "9987" }]);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seedJobId]);

    // Default empty row if no seed
    useEffect(() => {
        if (!seedJobId && lines.length === 0) {
            setLines([{ line_no: 1, line_type: "misc", description: "", quantity: 1, unit_price_minor: 0, tax_rate_percent: 18, hsn_sac_code: "9987" }]);
        }
    }, [seedJobId, lines.length]);

    const addLine = () => setLines([...lines, { line_no: lines.length + 1, line_type: "misc", description: "", quantity: 1, unit_price_minor: 0, tax_rate_percent: 18, hsn_sac_code: "9987" }]);
    const setLine = (i, patch) => setLines(lines.map((l, j) => (i === j ? { ...l, ...patch } : l)));
    const remove = (i) => setLines(lines.filter((_, j) => j !== i));

    const subtotal = useMemo(
        () => lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price_minor) || 0), 0),
        [lines],
    );
    const tax = useMemo(
        () => lines.reduce((acc, l) => acc + Math.round((Number(l.quantity) || 0) * (Number(l.unit_price_minor) || 0) * (Number(l.tax_rate_percent) || 0) / 100), 0),
        [lines],
    );

    const submit = async () => {
        if (!branchId || !customerId || lines.length === 0) { toast.error("Branch, customer and at least one line are required"); return; }
        if (lines.some((l) => !l.description || !l.quantity || l.unit_price_minor == null)) {
            toast.error("Every line needs description, qty and unit price"); return;
        }
        setSubmitting(true);
        try {
            const body = {
                branch_id: branchId, customer_id: customerId,
                job_card_id: jobCardId || null,
                lines: lines.map((l) => ({
                    line_type: l.line_type, description: l.description,
                    part_id: l.part_id || null,
                    quantity: Number(l.quantity), unit_price_minor: Number(l.unit_price_minor),
                    tax_rate_percent: Number(l.tax_rate_percent), hsn_sac_code: l.hsn_sac_code,
                    unit_of_measure: l.unit_of_measure || null,
                })),
            };
            const created = await EstimateAPI.create(body);
            toast.success(`Created estimate ${created.estimate_number}`);
            nav(`/estimates/${created.id}`);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="mx-auto max-w-5xl space-y-6" data-testid="estimate-new-page">
            <Link to="/estimates" className="inline-flex items-center text-sm text-zinc-500">
                <ArrowLeft className="mr-1 h-4 w-4" />Back to estimates
            </Link>
            <header>
                <h1 className="font-display text-3xl font-extrabold tracking-tight">New Estimate</h1>
                <p className="mt-1 text-sm text-zinc-500">
                    {prefilled ? "Pre-filled from job card. Add or trim as needed." : "Send for customer approval, then convert approved lines to a GST invoice."}
                </p>
            </header>
            <Card>
                <CardContent className="space-y-5 p-5">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1.5"><Label>Branch</Label>
                            <Select value={branchId} onValueChange={setBranchId}>
                                <SelectTrigger data-testid="estimate-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                                <SelectContent>{(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5"><Label>Customer</Label>
                            <Select value={customerId} onValueChange={setCustomerId}>
                                <SelectTrigger data-testid="estimate-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    {jobCardId && (
                        <div className="rounded-md border border-blue-300 bg-blue-50 p-3 text-xs dark:border-blue-700 dark:bg-blue-950/40" data-testid="estimate-from-job-banner">
                            Linked to job card <code className="font-mono">{jobCardId}</code>. Parts will be checked against actual consumption on conversion.
                        </div>
                    )}

                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <Label>Lines</Label>
                            <Button size="sm" variant="outline" onClick={addLine} data-testid="estimate-add-line-btn"><Plus className="mr-1 h-3 w-3" />Add line</Button>
                        </div>
                        <div className="space-y-2">
                            {lines.map((ln, idx) => (
                                <LineRow key={idx} idx={idx} ln={ln} setLine={setLine} remove={remove} ns="estimate-new" />
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-zinc-200 pt-3 text-sm font-mono dark:border-zinc-800">
                        <div className="text-zinc-500">{lines.length} line{lines.length !== 1 ? "s" : ""}</div>
                        <div className="space-x-3">
                            <span className="text-zinc-500">Subtotal {inr(subtotal)}</span>
                            <span className="text-zinc-500">Tax {inr(tax)}</span>
                            <span className="text-base font-bold" data-testid="estimate-new-total">Total {inr(subtotal + tax)}</span>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => nav("/estimates")}>Cancel</Button>
                        <Button onClick={submit} disabled={submitting} data-testid="estimate-new-submit"
                                className="bg-brand-red text-white hover:bg-brand-redDeep">
                            {submitting ? "Creating…" : "Save draft"}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function LineRow({ idx, ln, setLine, remove, ns }) {
    const [parts, setParts] = useState([]);
    const [search, setSearch] = useState("");
    useEffect(() => {
        if (ln.line_type !== "part") return;
        const t = setTimeout(() => {
            PartAPI.list({ q: search, page_size: 8 }).then((d) => setParts(d.items || []));
        }, 150);
        return () => clearTimeout(t);
    }, [search, ln.line_type]);

    return (
        <div className="grid grid-cols-12 items-end gap-2 rounded-md border border-zinc-200 p-2 dark:border-zinc-800"
             data-testid={`${ns}-line-${idx}`}>
            <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Type</Label>
                <Select value={ln.line_type} onValueChange={(v) => setLine(idx, { line_type: v })}>
                    <SelectTrigger data-testid={`${ns}-line-${idx}-type`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="labour">Labour</SelectItem>
                        <SelectItem value="part">Part</SelectItem>
                        <SelectItem value="misc">Misc</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="col-span-4">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Description</Label>
                {ln.line_type === "part" ? (
                    <Input list={`${ns}-parts-${idx}`} value={ln.description}
                           onChange={(e) => { setLine(idx, { description: e.target.value }); setSearch(e.target.value); }}
                           data-testid={`${ns}-line-${idx}-desc`} />
                ) : (
                    <Input value={ln.description} onChange={(e) => setLine(idx, { description: e.target.value })} data-testid={`${ns}-line-${idx}-desc`} />
                )}
                {ln.line_type === "part" && (
                    <datalist id={`${ns}-parts-${idx}`}>
                        {parts.map((p) => <option key={p.id} value={p.name}>{p.part_number}</option>)}
                    </datalist>
                )}
            </div>
            <div className="col-span-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Qty</Label>
                <Input type="number" value={ln.quantity} onChange={(e) => setLine(idx, { quantity: Number(e.target.value) })} data-testid={`${ns}-line-${idx}-qty`} />
            </div>
            <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Rate (paise)</Label>
                <Input type="number" value={ln.unit_price_minor} onChange={(e) => setLine(idx, { unit_price_minor: Number(e.target.value) })} data-testid={`${ns}-line-${idx}-rate`} />
            </div>
            <div className="col-span-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">GST</Label>
                <Select value={String(ln.tax_rate_percent)} onValueChange={(v) => setLine(idx, { tax_rate_percent: Number(v) })}>
                    <SelectTrigger data-testid={`${ns}-line-${idx}-gst`}><SelectValue /></SelectTrigger>
                    <SelectContent>{GST_SLABS.map((s) => <SelectItem key={s} value={String(s)}>{s}%</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div className="col-span-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">HSN</Label>
                <Input value={ln.hsn_sac_code} onChange={(e) => setLine(idx, { hsn_sac_code: e.target.value })} />
            </div>
            <div className="col-span-1 flex justify-end">
                <Button size="icon" variant="ghost" onClick={() => remove(idx)} data-testid={`${ns}-line-${idx}-remove`}>
                    <Trash2 className="h-4 w-4 text-brand-red" />
                </Button>
            </div>
        </div>
    );
}

// ===== Detail =====
export function EstimateDetailPage() {
    useTitle("Estimate");
    const { id } = useParams();
    const nav = useNavigate();
    const { permissions } = useAuth();
    const [est, setEst] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    const load = useCallback(async () => setEst(await EstimateAPI.get(id)), [id]);
    useEffect(() => { load(); }, [load]);

    if (!est) return <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>;

    const canEdit = est.status === "draft" && permissions.includes("estimate:update");
    const canSend = ["draft", "rejected", "sent_for_approval"].includes(est.status) && permissions.includes("estimate:approve");
    const canConvert = ["approved", "partially_approved"].includes(est.status) && permissions.includes("invoice:create");
    const canCancel = !["cancelled", "converted_to_invoice"].includes(est.status) && permissions.includes("estimate:delete");
    const publicUrl = est.customer_approval_token ? `${window.location.origin}/p/estimates/${est.customer_approval_token}` : "";

    const sendForApproval = async () => {
        try {
            await EstimateAPI.sendForApproval(est.id);
            toast.success("Sent for approval");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };
    const convert = async () => {
        try {
            const r = await EstimateAPI.convertToInvoice(est.id);
            toast.success(`Converted → ${r.invoice_number}`);
            nav(`/invoices/${r.invoice_id}`);
        } catch (e) { toast.error(extractError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="estimate-detail-page">
            <Link to="/estimates" className="inline-flex items-center text-sm text-zinc-500">
                <ArrowLeft className="mr-1 h-4 w-4" />Back
            </Link>
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Estimate</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="estimate-number">{est.estimate_number}</h1>
                    <div className="mt-1 text-sm text-zinc-500">
                        {est.customer_snapshot?.display_name} · {est.branch_snapshot?.code} · v{est.version}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span data-testid="estimate-status-pill"
                          className={`inline-flex rounded-md border px-3 py-1 text-sm font-mono ${STATUS_PILL[est.status] || ""}`}>
                        <span data-testid={`estimate-status-pill-${est.status}`}>{est.status.replace(/_/g, " ")}</span>
                    </span>
                    {canEdit && (
                        <Link to={`/estimates/${est.id}/edit`}>
                            <Button variant="outline" size="sm" data-testid="estimate-action-edit"><Edit3 className="mr-1 h-3.5 w-3.5" />Edit</Button>
                        </Link>
                    )}
                    {canSend && (
                        <Button onClick={sendForApproval} data-testid="estimate-action-send-approval"
                                className="bg-brand-red text-white hover:bg-brand-redDeep">
                            <Send className="mr-1 h-3.5 w-3.5" />Send for approval
                        </Button>
                    )}
                    {canConvert && (
                        <Button onClick={convert} data-testid="estimate-action-convert-invoice"
                                className="bg-brand-red text-white hover:bg-brand-redDeep">
                            <FileSignature className="mr-1 h-3.5 w-3.5" />Convert to invoice
                        </Button>
                    )}
                    {canCancel && (
                        <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="estimate-action-cancel">
                                    <XCircle className="mr-1 h-3.5 w-3.5 text-rose-600" />Cancel
                                </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="estimate-cancel-dialog">
                                <DialogHeader><DialogTitle>Cancel estimate?</DialogTitle></DialogHeader>
                                <Label>Reason</Label>
                                <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                                          data-testid="estimate-cancel-reason" />
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setCancelOpen(false)}>Back</Button>
                                    <Button className="bg-rose-600 text-white hover:bg-rose-700" data-testid="estimate-cancel-submit"
                                            onClick={() => toast.message("Cancel reason captured")}>
                                        Confirm cancel
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </header>

            {publicUrl && (
                <Card>
                    <CardContent className="flex items-center justify-between gap-3 p-4">
                        <div className="min-w-0">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Customer approval link</div>
                            <div className="truncate text-sm" data-testid="estimate-public-link">{publicUrl}</div>
                        </div>
                        <Button variant="outline" size="sm"
                                onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }}
                                data-testid="estimate-public-link-copy">
                            <Copy className="mr-1 h-3 w-3" />Copy
                        </Button>
                    </CardContent>
                </Card>
            )}

            <Tabs defaultValue="overview">
                <TabsList>
                    <TabsTrigger value="overview" data-testid="estimate-tab-overview"><FileText className="mr-1 h-3 w-3" />Overview</TabsTrigger>
                    <TabsTrigger value="lines" data-testid="estimate-tab-lines">Lines</TabsTrigger>
                    <TabsTrigger value="tax" data-testid="estimate-tab-tax">Tax breakdown</TabsTrigger>
                </TabsList>
                <TabsContent value="overview">
                    <Card>
                        <CardContent className="space-y-3 p-5">
                            <Row label="Customer" value={est.customer_snapshot?.display_name} />
                            <Row label="Customer GSTIN" value={est.customer_snapshot?.gstin || "—"} mono />
                            <Row label="Branch" value={`${est.branch_snapshot?.code} · ${est.branch_snapshot?.name}`} />
                            <Row label="Branch GSTIN" value={est.branch_snapshot?.gstin || "—"} mono />
                            <Row label="Place of supply" value={est.place_of_supply_state_code} mono />
                            <Row label="Supply type" value={est.place_of_supply_is_intra_state ? "Intra-state · CGST + SGST" : "Inter-state · IGST"} />
                            <Row label="Subtotal" value={inr(est.subtotal_minor)} mono />
                            <Row label="Tax" value={inr(est.tax_total_minor)} mono />
                            <Row label="Total" value={<b data-testid="estimate-total-display">{inr(est.total_minor)}</b>} />
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="lines">
                    <Card>
                        <CardContent className="p-0">
                            <Table data-testid="estimate-lines-table">
                                <TableHeader><TableRow>
                                    <TableHead>#</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead className="text-right">Qty</TableHead>
                                    <TableHead className="text-right">Rate</TableHead>
                                    <TableHead className="text-right">Taxable</TableHead>
                                    <TableHead className="text-right">Tax</TableHead>
                                    <TableHead className="text-right">Line total</TableHead>
                                    <TableHead>Customer</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {(est.lines || []).map((l) => (
                                        <TableRow key={l.line_id} data-testid={`estimate-line-row-${l.line_id}`}>
                                            <TableCell className="font-mono">{l.line_no}</TableCell>
                                            <TableCell>{l.description}</TableCell>
                                            <TableCell className="text-right font-mono">{l.quantity} {l.unit_of_measure || ""}</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.unit_price_minor)}</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.taxable_amount_minor)}</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.tax_total_minor)} ({l.tax_rate_percent}%)</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.line_total_minor)}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="font-mono text-[10px]"
                                                       data-testid={`estimate-line-approval-${l.line_id}`}>
                                                    {l.approval_status || "pending"}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="tax">
                    <TaxBreakdownTable doc={est} lines={est.lines || []} testid="estimate-tax-breakdown" />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex items-center justify-between border-b border-zinc-100 py-1.5 text-sm last:border-0 dark:border-zinc-800">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
            <span className={mono ? "font-mono" : ""}>{value}</span>
        </div>
    );
}

// ===== Edit (light optimistic-lock) =====
export function EstimateEditPage() {
    useTitle("Edit estimate");
    const { id } = useParams();
    const nav = useNavigate();
    const [est, setEst] = useState(null);
    useEffect(() => { EstimateAPI.get(id).then(setEst); }, [id]);
    if (!est) return <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>;
    if (est.status !== "draft") {
        return (
            <div className="mx-auto max-w-md py-12 text-center" data-testid="estimate-edit-blocked">
                <p className="text-sm text-zinc-600">Estimate is no longer in draft. Editing is disabled.</p>
                <Button className="mt-3" variant="outline" onClick={() => nav(`/estimates/${id}`)}>Back</Button>
            </div>
        );
    }
    return (
        <div className="mx-auto max-w-3xl py-8 text-center" data-testid="estimate-edit-page">
            <RotateCcw className="mx-auto h-10 w-10 text-zinc-300" />
            <h2 className="mt-4 font-display text-xl font-bold">Estimate editing</h2>
            <p className="mt-2 max-w-md mx-auto text-sm text-zinc-500">
                The estimate is locked by version <span className="font-mono">v{est.version}</span>. Use the new-estimate page
                to recreate or supersede this estimate. Inline line editing is coming soon.
            </p>
            <Button className="mt-4" onClick={() => nav(`/estimates/${id}`)}>Back to estimate</Button>
        </div>
    );
}
